import { useEffect, useRef, useState } from 'react'

interface VideoBackgroundProps {
  videoSrc: string
  fallbackImage?: string
  className?: string
}

export function VideoBackground({ 
  videoSrc, 
  fallbackImage = '/media/backgrounds/star-wars-galaxies-sci-fi-mmo.avif',
  className = ''
}: VideoBackgroundProps) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [isVideoReady, setIsVideoReady] = useState(false)
  const [useFallback, setUseFallback] = useState(false)
  const fallbackRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    // Preload fallback image to prevent flash - 2025-01-XX
    const preloadImage = new Image()
    preloadImage.src = fallbackImage

    // Set initial state - show fallback immediately to prevent flash - 2025-01-XX
    setIsVideoReady(false)
    setUseFallback(false)
    if (video.style) {
      video.style.opacity = '0'
    }
    // Show fallback image immediately
    if (fallbackRef.current) {
      fallbackRef.current.style.opacity = '1'
    }

    // Handle video ready to play - 2025-01-XX
    const handleCanPlay = () => {
      setIsVideoReady(true)
      // Fade in video smoothly and fade out fallback
      if (video.style) {
        video.style.transition = 'opacity 0.5s ease-in-out'
        video.style.opacity = '1'
      }
      if (fallbackRef.current) {
        fallbackRef.current.style.opacity = '0'
      }
    }

    // Handle video loaded data - 2025-01-XX
    const handleLoadedData = () => {
      // Video metadata loaded, start playing
      video.play().catch(error => {
        console.warn('Video autoplay failed:', error)
        setUseFallback(true)
        if (video.style) {
          video.style.opacity = '0'
        }
      })
    }

    // Handle video load errors - 2025-01-XX
    const handleError = () => {
      console.warn('Video failed to load, falling back to image')
      setUseFallback(true)
      if (video.style) {
        video.style.opacity = '0'
      }
    }

    // Add event listeners
    video.addEventListener('canplay', handleCanPlay)
    video.addEventListener('loadeddata', handleLoadedData)
    video.addEventListener('error', handleError)
    
    // Load the video
    video.load()
    
    return () => {
      video.removeEventListener('canplay', handleCanPlay)
      video.removeEventListener('loadeddata', handleLoadedData)
      video.removeEventListener('error', handleError)
      // Reset video state on unmount to prevent flash - 2025-01-XX
      if (video) {
        if (video.style) {
          video.style.opacity = '0'
        }
        video.pause()
        video.src = ''
      }
    }
  }, [videoSrc, fallbackImage])

  return (
    <>
      {/* Fallback image background - shows immediately to prevent flash - 2025-01-XX */}
      <div 
        ref={fallbackRef}
        className="video-background"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('${fallbackImage}')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          opacity: isVideoReady ? 0 : 1,
          transition: 'opacity 0.5s ease-in-out',
          pointerEvents: 'none'
        }}
      />
      
      {/* Video element - fades in once ready - 2025-01-XX */}
      <video
        ref={videoRef}
        className={`video-background ${className}`}
        loop
        muted
        playsInline
        preload="auto"
        style={{
          opacity: isVideoReady && !useFallback ? 1 : 0,
          transition: 'opacity 0.5s ease-in-out'
        }}
      >
        <source src={videoSrc} type="video/webm" />
        <source src={videoSrc.replace('.webm', '.mp4')} type="video/mp4" />
      </video>
      <div className="video-overlay" />
    </>
  )
}
